import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ActionButton from './UI/ActionButton';
import DocumentTile, { DOCUMENT_GRID } from './UI/DocumentTile';
import { cardClass } from './UI/dashboardCard';

// Whole-type matching: "Proof of Residence" contains "id" (res-id-ence), so a
// substring test for ID would pick up the proof of residence.
const isIdType = (t) => {
  const x = String(t || '').trim().toLowerCase();
  return x === 'identification' || x === 'id' || x === 'id document' || x.includes('passport') || x.includes('identification');
};
const isPorType = (t) => ['proof of residence', 'proof of address', 'por'].includes(String(t || '').trim().toLowerCase());

// Shows document checklist for the most recent Pending/Awaiting Client online loan.
// Props:
// - loans: array
// - onContinue: fn(loan) to open application modal/flow
const REQUIRED_TYPES = ['Bank Statement', 'Payslip'];

const DocumentsHubCard = ({ loans = [], onContinue, className = '' }) => {
  const [docs, setDocs] = useState([]); // loan documents for latest/active loan
  const [loading, setLoading] = useState(false);
  const [activeLoan, setActiveLoan] = useState(null);
  const [clientDocs, setClientDocs] = useState([]); // client-wide documents (e.g., Identification, Proof Of Residence)
  const [clientLoading, setClientLoading] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);

  // Determine latest loan (prefer created_at, fallback to id)
  const latestLoan = useMemo(() => {
    if (!Array.isArray(loans) || loans.length === 0) return null;
    const withDate = loans.filter(l => l.created_at);
    if (withDate.length > 0) {
      return [...withDate].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    }
    return [...loans].sort((a, b) => (b.id || 0) - (a.id || 0))[0];
  }, [loans]);

  useEffect(() => {
    setActiveLoan(latestLoan || null);
  }, [latestLoan]);

  // Load documents for the latest loan
  useEffect(() => {
    const loadDocs = async () => {
      if (!activeLoan?.id) { setDocs([]); return; }
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/document/loan/${activeLoan.id}`), { headers: { Authorization: `Bearer ${token}` } });
        const arr = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.documents) ? res.data.documents : []);
        setDocs(arr);
      } catch (e) {
        setDocs([]);
      } finally {
        setLoading(false);
      }
    };
    loadDocs();
  }, [activeLoan]);

  // Load client profile to get client ID, then fetch client-level documents
  useEffect(() => {
    const loadClientDocs = async () => {
      setClientLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) { setClientDocs([]); return; }
        // Fetch client profile to determine client id
        const profileRes = await axios.get(apiUrl('/api/clients/my-profile'), { headers: { Authorization: `Bearer ${token}` } });
        const id = profileRes?.data?.personal_info?.id || profileRes?.data?.id || null;
        setClientId(id);
        if (!id) { setClientDocs([]); return; }
        // Fetch all client documents
        const cRes = await axios.get(apiUrl(`/api/document/client/${id}`), { headers: { Authorization: `Bearer ${token}` } });
        const list = Array.isArray(cRes.data) ? cRes.data : (Array.isArray(cRes.data?.documents) ? cRes.data.documents : []);
        setClientDocs(list || []);
      } catch (e) {
        setClientDocs([]);
      } finally {
        setClientLoading(false);
      }
    };
    loadClientDocs();
  }, []);

  const refreshClientDocs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !clientId) return;
      const cRes = await axios.get(apiUrl(`/api/document/client/${clientId}`), { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(cRes.data) ? cRes.data : (Array.isArray(cRes.data?.documents) ? cRes.data.documents : []);
      setClientDocs(list || []);
    } catch {}
  };

  const presentTypes = new Set((docs || []).map(d => (d.document_type || '').toLowerCase()));
  const clientPresentTypes = new Set((clientDocs || []).map(d => (d.document_type || '').toLowerCase()));
  
  // Check for loan-specific documents
  const missing = REQUIRED_TYPES.filter(t => !presentTypes.has(t.toLowerCase()));
  
  // Check for client-level documents (identification and proof of residence)
  const hasIdentification = [...clientPresentTypes].some(isIdType);
  const hasProofOfResidence = [...clientPresentTypes].some(isPorType);
  const identificationMissing = !hasIdentification;
  const proofOfResidenceMissing = !hasProofOfResidence;

  const canContinue = activeLoan && activeLoan.application_type === 'Online' && (activeLoan.status === 'Pending' || activeLoan.status === 'Awaiting Client');

  const uploadClientDocument = async (file, docType) => {
    if (!file || !clientId) return;
    setUploadingType(docType);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      // API expects field name 'contract' for file payloads throughout the app
      formData.append('contract', file);
      formData.append('client_id', clientId);
      // Normalize UI label to server-accepted document_type (matches DB CHECK constraint)
      const normalizeType = (s) => {
        const t = String(s || '').toLowerCase();
        if (t === 'proof of residence' || t === 'proof of address' || t === 'por') return 'Proof Of Residence';
        if (t === 'identification' || t === 'id' || t.includes('identification')) return 'Identification';
        if (t === 'online client photo' || t === 'photo' || t === 'client photo') return 'Online Client Photo';
        if (t === 'signed contract' || t.includes('contract') || t.includes('agreement')) return 'Signed Contract';
        if (t === 'bank statement' || t.includes('statement')) return 'Bank Statement';
        if (t === 'payslip' || t === 'pay slip') return 'Payslip';
        return s;
      };
      formData.append('document_type', normalizeType(docType));
      const resp = await fetch(apiUrl('/api/document/upload'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!resp.ok) throw new Error('Upload failed');
      await refreshClientDocs();
    } catch (e) {
      alert('Failed to upload document');
    } finally {
      setUploadingType(null);
    }
  };

  const newest = (list) => [...list].sort((x, y) => (new Date(y.created_at || 0)) - (new Date(x.created_at || 0)) || (y.id || 0) - (x.id || 0))[0];
  const idDoc = newest((clientDocs || []).filter((d) => isIdType(d.document_type)));
  const porDoc = newest((clientDocs || []).filter((d) => isPorType(d.document_type)));
  const loanDocs = [];
  for (const type of ['Payslip', 'Bank Statement', 'Signed Contract']) {
    const d = newest((docs || []).filter((x) => String(x.document_type || '').toLowerCase() === type.toLowerCase()));
    if (d) loanDocs.push({ type, doc: d });
    else if (REQUIRED_TYPES.includes(type) && activeLoan?.application_type === 'Online') loanDocs.push({ type, doc: null });
  }
  const loanRef = activeLoan ? (activeLoan.loan_code || `Loan #${activeLoan.id}`) : null;

  return (
    <div className={`${cardClass('indigo')} p-4 pt-5 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100">Documents Hub</h3>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            {loanRef ? `Your documents, and those of ${loanRef} · ${activeLoan.status}` : 'Your documents'}
          </p>
        </div>
        {canContinue && (
          <ActionButton
            onClick={() => typeof onContinue === 'function' && onContinue(activeLoan)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >Open Application</ActionButton>
        )}
      </div>

      {(loading || clientLoading) && !(clientDocs || []).length && !(docs || []).length ? (
        <div className="h-24 bg-gray-100 dark:bg-slate-700 rounded animate-pulse" />
      ) : (
        <div className={DOCUMENT_GRID}>
          {['Identification', 'Proof of Residence'].map((type) => {
            const doc = type === 'Identification' ? idDoc : porDoc;
            return (
              <DocumentTile key={type} type={type} doc={doc} required
                onPick={doc ? undefined : (file) => uploadClientDocument(file, type)} uploading={uploadingType === type} />
            );
          })}
          {loanDocs.map(({ type, doc }) => (
            <DocumentTile key={type} type={type} doc={doc} required />
          ))}
        </div>
      )}
      {!activeLoan && (
        <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">No loans yet — your loan documents will show here once you apply.</p>
      )}
    </div>
  );
};

export default DocumentsHubCard;
